import { type ReactElement, type ReactNode } from 'react';
import { usePermissions } from '../../permissions/usePermissions';
import { type PermissionId } from '../../permissions/permissions';
import Unauthorized from '../common/Unauthorized';

interface RequirePermissionProps {
    // The permission the user needs read access to. Omit and set superAdmin to gate on super-admin only.
    permission?: PermissionId;
    // When true, only super admins may view the route (used for the admin-only pages).
    superAdmin?: boolean;
    children: ReactNode;
}

// Route guard: renders the page only if the user is allowed to see it, otherwise the Unauthorized page.
//
// The two props are ANDed, not alternatives. `superAdmin` used to short-circuit the whole check, so passing
// both silently ignored the permission — a trap for anyone gating an admin page on a specific grant. A page
// that is both TouchTab-only AND separately grantable (Admin > Deleted Locations) needs both to hold.
//
// Super admins are NOT exempt from `permission`: since TTD-129 they are subject to their own permissions, so
// a page gated this way is invisible until the permission is actually granted to a role they hold.
const RequirePermission = ({ permission, superAdmin, children }: RequirePermissionProps): ReactElement => {
    const { canRead, isSuperAdmin } = usePermissions();

    const meetsSuperAdmin = !superAdmin || isSuperAdmin;
    const meetsPermission = !permission || canRead(permission);
    const allowed = meetsSuperAdmin && meetsPermission;

    return <>{allowed ? children : <Unauthorized />}</>;
};

export default RequirePermission;
